'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { setCookie, deleteCookie } from 'cookies-next';
import {
  IFilterConfig,
  TFilterValues,
  SetFilterFn,
  ISortOption,
} from '../or-filtered-grid.types';

interface IUseFiltersProps<T> {
  data: T[]; // The full list of data to filter/sort
  config: IFilterConfig<T>[]; // Filter configuration (e.g., keys, default values, filter functions)
  sortOptions: ISortOption<T>[]; // Sort configuration (e.g., sort key, sort function)
  initialSort: string; // Default sort key
  designStudioId?: number; // Optional ID used for context-specific sorting
}

const MAX_AGE = 60 * 60 * 24 * 365;
const COOKIE_PATH = '/';
const COOKIE_OPTIONS = { path: COOKIE_PATH, maxAge: MAX_AGE };

// Parse cookies from document.cookie
const getCookies = (): Record<string, string> => {
  if (typeof document === 'undefined') return {};
  return document.cookie
    .split('; ')
    .map((c) => c.split('='))
    .reduce(
      (acc, [k, v]) => {
        acc[k] = decodeURIComponent(v || '');
        return acc;
      },
      {} as Record<string, string>,
    );
};

/**
 * useFilters
 *
 * Hook to manage UI filter/sort state for a dataset, including:
 * - initializing filter/sort state from cookies
 * - applying custom filter/sort logic
 * - persisting user choices in cookies
 */
export const useFilters = <T>({
  data,
  config,
  sortOptions,
  initialSort,
  designStudioId,
}: IUseFiltersProps<T>) => {
  /**
   * Generates default values for all filters based on config.
   */
  const getDefaultFilterValues = (): TFilterValues =>
    Object.fromEntries(
      config.map((c) => [c.key, c.default ?? '']),
    ) as TFilterValues;

  // Validate component is hydrated
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  // Local state for current filter values
  const [filterValues, setFilterValues] = useState<TFilterValues>(() =>
    getDefaultFilterValues(),
  );

  const [sort, setSort] = useState<string>(() => {
    const cookies = getCookies();
    return cookies['Sort'] ?? initialSort ?? sortOptions[0]?.value ?? '';
  });

  // Hydrate filters from cookies
  useEffect(() => {
    const cookies = getCookies();
    const hydrated: TFilterValues = { ...getDefaultFilterValues() };

    config.forEach(({ key, deserialize }) => {
      const raw = cookies[key];

      if (raw !== undefined) {
        let parsed: any = raw;

        if (deserialize) {
          parsed = deserialize(raw);
        } else if (raw === 'true') {
          parsed = true;
        } else if (raw === 'false') {
          parsed = false;
        } else if (/^\[.*\]$/.test(raw)) {
          try {
            parsed = JSON.parse(raw);
          } catch {
            parsed = raw;
          }
        }

        hydrated[key] = parsed;
      }
    });

    setFilterValues(hydrated);
    setFiltersHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  /**
   * Updates a specific filter value and syncs it to cookies.
   * Supports string, boolean, or array values
   */
  const setFilter: SetFilterFn<TFilterValues> = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));

    if (
      value === '' ||
      value === false ||
      (Array.isArray(value) && value.length === 0)
    ) {
      deleteCookie(key);
    } else {
      const serialized = Array.isArray(value)
        ? JSON.stringify(value)
        : String(value);
      setCookie(key, serialized, COOKIE_OPTIONS);
    }
  };

  /**
   * Resets all filters to their default values and clears related cookies.
   */
  const resetFilters = () => {
    const defaults = getDefaultFilterValues();
    setFilterValues(defaults);
    config.forEach(({ key }) => deleteCookie(key));
  };

  /**
   * Updates sort value and stores it in a cookie.
   */
  const onSortChange = (value: string) => {
    setSort(value);
    setCookie('Sort', value, COOKIE_OPTIONS);
  };

  /**
   * Memoized sorted version of the dataset, using the active sort key.
   */
  const sortedData = useMemo(() => {
    const sortFn = sortOptions.find((opt) => opt.value === sort)?.sortFn;
    return sortFn
      ? [...data].sort((a, b) => sortFn(a, b, designStudioId))
      : data;
  }, [data, sort, sortOptions, designStudioId]);

  /**
   * Whether any filters are currently active (i.e., non-empty or truthy).
   */
  const hasActiveFilters = useMemo(
    () =>
      Object.values(filterValues).some((v) => {
        if (Array.isArray(v)) return v.length > 0;
        return v !== '' && v !== false;
      }),
    [filterValues],
  );

  /**
   * Specific logic to check if the `ExteriorStyle` filter is empty.
   * This allows for custom treatment of that filter in filtering logic.
   */
  const isExteriorStyleEmpty = useMemo(() => {
    const value = filterValues?.ExteriorStyle;

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    if (typeof value === 'string') {
      return value.trim() === '';
    }

    return true;
  }, [filterValues]);

  /**
   * Checks whether a single data item matches all active filters.
   */
  const matchesAllFilters = useCallback(
    (item: T) =>
      config.every(({ key, filterFn }) => {
        const filterValue = filterValues[key];
        // If the filter value is an empty array (multi-select "All"), skip filtering
        if (Array.isArray(filterValue) && filterValue.length === 0) {
          return true;
        }
        // If the filter value is an empty string (single-select "All"), skip filtering
        if (filterValue === '') {
          return true;
        }

        // Otherwise, apply the filter function
        //console.debug('Applying Filter: ', key, item, filterValue);

        return filterFn(item, filterValue, designStudioId);
      }),
    [config, filterValues, designStudioId],
  );

  const isPreferred = useCallback(
    (item: T) => (item as any)?.FloorPlans?.[0]?.IsPreferred === true,
    [],
  );

  /**
   * Memoized list of filtered data based on active filters and sort.
   * If no filters are active, only return preferred items.
   */
  const filteredData = useMemo(() => {
    if (!filtersHydrated) return [];

    const base = hasActiveFilters
      ? sortedData.filter(matchesAllFilters)
      : sortedData;

    return isExteriorStyleEmpty ? base.filter(isPreferred) : base;
  }, [
    filtersHydrated,
    sortedData,
    matchesAllFilters,
    hasActiveFilters,
    isExteriorStyleEmpty,
    isPreferred,
  ]);

  // Filtered count to avoid data issue for now where there is no preferred elevation for a series
  // IMPORTANT: This will only help for the count and for exterior styles.
  // It WILL NOT fix other filters where the results are off because there is no preferred elevation
  const seriesUnique = new Set<number>();
  const filteredCount = filteredData.reduce((prev, current: any) => {
    if (current?.Plan?.Id) {
      if (!seriesUnique.has(current.Plan.Id)) {
        seriesUnique.add(current.Plan.Id);
        return prev + 1;
      }
    }

    // Default
    return prev;
  }, 0);

  return {
    filterValues,
    setFilter,
    resetFilters,
    filteredData,
    sort,
    setSort: onSortChange,
    hasActiveFilters,
    filteredCount,
  };
};
